import React, { useEffect, useMemo, useState } from "react";
import { useProposal } from "../../context/ProposalContext";
import { useRates } from "../../context/RatesContext";
import {
  calculateEstimate,
  computeTotals,
  estimatedVotersFor,
  formatCurrency,
  ELECTION_TYPES,
  BASE_INCLUSIONS,
  NOMINATION_INCLUSION,
} from "../../lib/costEngine";
import { SectionTable } from "./CostRow";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { ArrowLeft, ArrowRight, Users, Vote, Globe, Plus, RotateCcw, Check } from "lucide-react";

const round2 = (n) => Math.round((n + Number.EPSILON) * 100) / 100;

const newId = () =>
  typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).slice(2);

const withIds = (arr) => arr.map((it) => ({ ...it, id: it.id || newId() }));

export default function Step2CostEstimate({ onNext, onBack }) {
  const { state, dispatch } = useProposal();
  const { rates } = useRates();
  const { event } = state;
  const typeLabel = ELECTION_TYPES[event.electionType]?.label || "—";

  const [items, setItems] = useState(() =>
    withIds(state.items && state.items.length ? state.items : calculateEstimate(event, rates).items)
  );

  useEffect(() => {
    dispatch({ type: "SET_ITEMS", items });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- dispatch is stable; persist when items change
  }, [items]);

  const totals = useMemo(() => computeTotals(items, rates.taxRate || 0), [items, rates]);
  const estimatedVoters = estimatedVotersFor(event.membershipSize, event.voterTurnout);

  const updateItem = (id, field, value) => {
    setItems((prev) =>
      prev.map((it) => {
        if (it.id !== id) return it;
        const next = { ...it, [field]: value };
        if (field === "qty" || field === "unit") {
          next.amount = round2((Number(next.qty) || 0) * (Number(next.unit) || 0));
        }
        return next;
      })
    );
  };

  const addItem = () =>
    setItems((prev) => [
      ...prev,
      { id: newId(), name: "New line item", qty: 1, unit: 0, amount: 0, section: "variable" },
    ]);

  const removeItem = (id) => setItems((prev) => prev.filter((it) => it.id !== id));

  const resetAuto = () => setItems(withIds(calculateEstimate(event, rates).items));

  const fixedItems = items.filter((i) => i.section === "fixed");
  const variableItems = items.filter((i) => i.section !== "fixed");
  const isElectionType = event.electionType === "election" || event.electionType === "by_election";
  const inclusions = isElectionType ? [NOMINATION_INCLUSION, ...BASE_INCLUSIONS] : BASE_INCLUSIONS;

  const summary = [
    { icon: Users, label: "Membership", value: Number(event.membershipSize).toLocaleString() },
    { icon: Vote, label: "Est. Voters", value: estimatedVoters.toLocaleString() },
    { icon: Globe, label: "Intl. Voters", value: Number(event.internationalVoters || 0).toLocaleString() },
  ];

  return (
    <div className="space-y-6" data-testid="cost-estimate-step">
      <div>
        <h2 className="font-display font-700 text-2xl text-foreground">Proposed Estimated Budget</h2>
        <p className="text-muted-foreground text-sm mt-1">
          A proposed estimate for{" "}
          <span className="font-medium text-foreground">{event.eventTitle || "your event"}</span> ({typeLabel}).
          Subject to change based on confirmed event details. Edit any line — totals update live.
        </p>
      </div>

      <div className="grid grid-cols-3 gap-3">
        {summary.map((s) => (
          <Card key={s.label} className="bg-accent/40 border-accent">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-primary">
                <s.icon className="h-4 w-4" />
                <span className="text-xs font-medium text-muted-foreground">{s.label}</span>
              </div>
              <p className="font-display font-700 text-lg text-foreground mt-1 truncate">{s.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="flex items-center justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={resetAuto} data-testid="reset-estimate-btn" className="gap-1.5">
          <RotateCcw className="h-3.5 w-3.5" /> Reset to auto
        </Button>
        <Button variant="outline" size="sm" onClick={addItem} data-testid="add-item-btn" className="gap-1.5">
          <Plus className="h-3.5 w-3.5" /> Add variable line item
        </Button>
      </div>

      <SectionTable
        title="Fixed Budget Estimate"
        rows={fixedItems}
        subtotal={totals.fixedSubtotal}
        subtotalTestId="fixed-subtotal"
        onUpdate={updateItem}
        onRemove={removeItem}
        preamble={
          <div className="px-4 py-3 border-b border-border bg-white" data-testid="fixed-inclusions">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
              The base fixed cost includes:
            </p>
            <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-1">
              {inclusions.map((inc) => (
                <li key={inc} className="flex items-start gap-2 text-sm text-foreground">
                  <Check className="h-3.5 w-3.5 text-primary mt-0.5 shrink-0" />
                  <span>{inc}</span>
                </li>
              ))}
            </ul>
          </div>
        }
      />

      <SectionTable
        title="Variable Budget Estimate"
        rows={variableItems}
        subtotal={totals.variableSubtotal}
        subtotalTestId="variable-subtotal"
        onUpdate={updateItem}
        onRemove={removeItem}
      />

      <Card className="bg-primary text-primary-foreground">
        <CardContent className="p-5 space-y-3">
          <div className="flex items-center justify-between text-sm opacity-90">
            <span>Subtotal (Fixed + Variable)</span>
            <span data-testid="estimate-subtotal" className="tabular-nums">{formatCurrency(totals.subtotal)}</span>
          </div>
          <div className="flex items-center justify-between text-sm opacity-90">
            <span>GST / Tax ({(totals.taxRate * 100).toFixed(totals.taxRate ? 2 : 0)}%)</span>
            <span data-testid="estimate-tax" className="tabular-nums">{formatCurrency(totals.tax)}</span>
          </div>
          <div className="flex items-center justify-between pt-3 border-t border-primary-foreground/20">
            <div>
              <p className="font-display font-700 text-lg">Total Estimated Budget</p>
              <p className="text-xs opacity-80">Excludes travel &amp; travel-related costs</p>
            </div>
            <p data-testid="estimate-total" className="font-display font-700 text-2xl tabular-nums">
              {formatCurrency(totals.total)}
            </p>
          </div>
        </CardContent>
      </Card>

      <p className="text-xs text-muted-foreground leading-relaxed">
        This is a proposed estimated budget for discussion purposes only and is subject to change based on
        information provided (or not provided) by the Client. It does not constitute a binding commitment of any
        kind. A final budget will follow in a Contract for Services upon confirmation of the voting event details
        and its governing regulations.
      </p>

      <div className="flex justify-between pt-2">
        <Button variant="outline" data-testid="back-to-details-btn" onClick={onBack} className="gap-2">
          <ArrowLeft className="h-4 w-4" /> Back
        </Button>
        <Button
          data-testid="next-to-proposal-btn"
          onClick={onNext}
          className="gap-2 transition-transform hover:-translate-y-0.5 hover:shadow-md"
        >
          Continue to Recipient Info <ArrowRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
